import { Box, Card, TextField, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { users } from '../../api/swagger'
import { isEmailValid } from '../../helpers/validateEmail'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import useLoadingContext from '../../hooks/useLoadingContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { UserProfile } from '../../swagger'
import { useUser } from '../../UserContext'
import OutlinedButton, {
  OutlinedButtonVariant,
} from '../Buttons/OutlinedButton'
import TextButton, { TextButtonVariant } from '../Buttons/TextButton'
import CircularProgressOverlay from '../Elements/CircularProgressOverlay'
import Header, { HeaderVariant } from '../Elements/Header'
import ChangeEmailModal from '../Modals/ChangeEmailModal'
import { useAuth } from '../Routes/AuthProvider'
import CardFormHeader from './CardFormHeader'

interface UserEmailAddressCardProps {
  userInfo: UserProfile
}

const UserEmailAddressCard: React.FunctionComponent<
  UserEmailAddressCardProps
> = (props) => {
  const { permissionAbility } = useAuth()
  const { t } = useTranslation()
  const theme = useTheme()
  const { setUser } = useUser()
  const { handleError } = useAppSnackbar()
  const {
    AccountSettingsTab: { editBusinessEmail: editBusinessEmailLoadingId },
  } = useLoadingIds()
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false)
  const [isInEditMode, setIsInEditMode] = useState(false)
  const [hasBusinessEmailError, setHasBusinessEmailError] = useState(false)
  const [businessEmail, setBusinessEmail] = useState('')
  const { userInfo } = props

  const invalidBusinessEmailError = t(
    'AccountSettingsTab.InvalidBusinessEmail',
    'Invalid business email. Email must be a valid address and not be classicalconversations.com domain.'
  )
  const businessEmailHelperText = t(
    'AccountSettingsTab.BusinessEmailHelperText',
    'This secondary email will be used for business emails within CC Connected'
  )
  const defaultFetchEditBusinessEmailErrorMessage = t(
    'AccountSettingsTab.FetchEditBusinessEmail.Error',
    'There was an error updating the business email. Please try again later.'
  )

  const onChangeEmailModalClose = () => {
    setIsChangeEmailModalOpen(false)
  }

  const canViewBusinessEmail = permissionAbility.can(
    'viewBusinessEmail',
    'User'
  )

  const isValidBusinessEmail = (): boolean => {
    return (
      isEmailValid(businessEmail) &&
      !/@(?:.*\.)?classicalconversations\.com$/i.test(businessEmail.trim())
    )
  }

  const editBusinessEmail = async () => {
    try {
      if (businessEmail && !isValidBusinessEmail()) {
        setHasBusinessEmailError(true)
        throw new Error(invalidBusinessEmailError)
      }

      const trimmedEmail = businessEmail.trim()

      setIsInEditMode(false)

      await users.editBusinessEmail({
        userId: `${userInfo.id}`,
        body: {
          businessEmail: trimmedEmail,
        },
      })

      setUser({
        ...userInfo,
        businessEmail: trimmedEmail,
      })
    } catch (error) {
      await handleError(error, defaultFetchEditBusinessEmailErrorMessage)
    }
  }

  const { triggerFetch: saveBusinessEmail, isLoading } = useLoadingContext({
    asyncFunction: editBusinessEmail,
    loadingId: editBusinessEmailLoadingId,
  })

  const cancelEditBusinessEmail = () => {
    setHasBusinessEmailError(false)
    setIsInEditMode(false)
    setBusinessEmail(props.userInfo.businessEmail || '')
  }

  useEffect(() => {
    if (props.userInfo.businessEmail) {
      setBusinessEmail(props.userInfo.businessEmail)
    }
  }, [props.userInfo.businessEmail])

  const onBusinessEmailChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (isInEditMode) {
      setHasBusinessEmailError(false)
      setBusinessEmail(e.target.value)
    }
  }

  return (
    <>
      <CircularProgressOverlay isLoading={isLoading} />
      <ChangeEmailModal
        isOpen={isChangeEmailModalOpen}
        onClose={onChangeEmailModalClose}
      />
      <Card>
        <CardFormHeader
          header={
            <Header
              variant={HeaderVariant.Card}
              id="emailAddress"
              headerName={t('AccountSettingsTab.EmailAddress', 'Email Address')}
              component="h3"
            />
          }
          headerContainerProps={{ paddingX: 1 }}
          menuContainerCss={{ padding: 2 }}
          buttons={
            canViewBusinessEmail &&
            (isInEditMode ? (
              <>
                <TextButton
                  id="cancel"
                  onClick={cancelEditBusinessEmail}
                  variant={TextButtonVariant.Cancel}
                  disabled={isLoading}
                />
                <OutlinedButton
                  id={OutlinedButtonVariant.SaveBusinessEmail}
                  variant={OutlinedButtonVariant.SaveBusinessEmail}
                  onClick={saveBusinessEmail}
                  disabled={isLoading}
                />
              </>
            ) : (
              <OutlinedButton
                id={OutlinedButtonVariant.EditBusinessEmail}
                variant={OutlinedButtonVariant.EditBusinessEmail}
                onClick={() => setIsInEditMode(true)}
              />
            ))
          }
        />
        <Box
          mb={4}
          sx={{
            padding: theme.spacing(0, 3, 3),
          }}
        >
          <Box
            display="grid"
            gap={2}
            sx={{
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr',
              },
            }}
          >
            <Box width="100%">
              <TextField
                fullWidth
                id="loginEmailField"
                label={t('AccountSettingsTab.LoginEmail', 'Login Email')}
                variant="filled"
                disabled
                value={userInfo.username}
              />
            </Box>
            {canViewBusinessEmail && (
              <Box width="100%">
                <TextField
                  fullWidth
                  id="businessEmail"
                  label={t(
                    'AccountSettingsTab.BusinessEmail',
                    'Business Email'
                  )}
                  variant="filled"
                  helperText={
                    hasBusinessEmailError
                      ? invalidBusinessEmailError
                      : businessEmailHelperText
                  }
                  disabled={!isInEditMode}
                  value={businessEmail}
                  onChange={onBusinessEmailChange}
                  error={hasBusinessEmailError}
                />
              </Box>
            )}
            <Box width="100%">
              {userInfo.email && (
                <TextField
                  fullWidth
                  id="classicalConversationsEmail"
                  label={t(
                    'AccountSettingsTab.ClassicalConversationsEmail',
                    'Classical Conversations Email'
                  )}
                  variant="filled"
                  helperText={t(
                    'AccountSettingsTab.EmailHelperText',
                    'This secondary email was created for you by the CC team'
                  )}
                  disabled
                  defaultValue={userInfo.email}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Card>
    </>
  )
}

export default UserEmailAddressCard
